/**
 * Blog Page — home no estilo portal de conteúdo.
 *
 * Modo "vitrine" (sem busca/filtro), de cima para baixo:
 *   1. Destaque (2/3) + Recentes (1/3), com a mesma altura;
 *   2. faixa de Recomendados (até 3, formato compacto);
 *   3. grade geral de artigos em 3 colunas no desktop.
 * Destaque e Recomendados vêm marcados no Admin (`highlight`); sem marcação, o
 * destaque é o artigo mais recente. Recentes e Recomendados são atalhos: os
 * mesmos artigos continuam aparecendo na grade geral.
 *
 * Modo "resultados" (busca ou categoria selecionada): grid de artigos filtrado.
 */
import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, BookOpen, Clock, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBlogPosts, useBlogCategories, useBlogHighlights } from '@/hooks/use-learning';
import { resolveMediaUrl } from '@/lib/api';
import { coverPositionStyle } from '@/lib/cover';
import type { BlogPost, BlogCategoryOption } from '@/types/learning';
import { formatBrasilia } from '@/lib/brasiliaDate';

const ALL_CATEGORIES = 'all';
/** Quantos artigos a coluna "Recentes" lista ao lado do destaque. */
const RECENT_COUNT = 4;

/** "27 de julho" — data curta usada no rodapé dos cards. */
function formatPublishedAt(isoDate: string): string {
  return formatBrasilia(isoDate, "dd 'de' MMMM");
}

/**
 * Props que tornam o card inteiro clicável mantendo o acesso por teclado
 * (Enter/Espaço), já que o elemento raiz é uma `div` e não um link.
 */
function clickableCardProps(onActivate: () => void) {
  return {
    role: 'link',
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate();
      }
    },
  } as const;
}

export default function BlogPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState<string | undefined>(undefined);
  const navigate = useNavigate();

  const { data, isLoading } = useBlogPosts(searchTerm, page, activeCategory);
  const { data: categoriesData } = useBlogCategories();
  const { data: highlights } = useBlogHighlights();
  const categories = categoriesData?.categories ?? [];

  const isFiltering = !!searchTerm || !!activeCategory;
  const posts = data?.posts ?? [];

  const readPost = (slug: string) => navigate(`/learn/blog/${slug}`);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleCategory = (slug: string | undefined) => {
    setActiveCategory(slug);
    setSearchTerm('');
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-[1600px] pb-12">
      {/* Cabeçalho + busca/seletor */}
      <div className="p-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-primary" />
            Blog
          </h1>
          <p className="text-muted-foreground mt-1">Artigos e dicas sobre finanças pessoais</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar artigos..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchTerm.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => handleSearch('')}
                aria-label="Limpar busca"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Select
            value={activeCategory ?? ALL_CATEGORIES}
            onValueChange={(value) => handleCategory(value === ALL_CATEGORIES ? undefined : value)}
          >
            <SelectTrigger className="w-full sm:w-52" aria-label="Filtrar por categoria">
              <SelectValue placeholder="Todas as categorias" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CATEGORIES}>Todas as categorias</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.slug} value={cat.slug}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {(() => {
        if (isLoading) return (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
        if (isFiltering) return (
            <ResultsView
              posts={posts}
              categories={categories}
              activeCategory={activeCategory}
              searchTerm={searchTerm}
              totalPages={data?.total_pages ?? 1}
              page={page}
              onPage={setPage}
              onClear={() => handleCategory(undefined)}
              onRead={readPost}
            />
        );
        if (posts.length === 0) return (
            <div className="px-6">
              <EmptyState message="Ainda não há artigos publicados." />
            </div>
        );
        return (
            <ShowcaseView
              posts={posts}
              featured={highlights?.featured ?? null}
              recommended={highlights?.recommended ?? []}
              onRead={readPost}
            />
        );
      })()}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Vitrine: Destaque + Recentes (topo) e grid de artigos              */
/* ------------------------------------------------------------------ */

function ShowcaseView({
  posts,
  featured,
  recommended,
  onRead,
}: {
  posts: BlogPost[];
  featured: BlogPost | null;
  recommended: BlogPost[];
  onRead: (slug: string) => void;
}) {
  // Sem artigo marcado como Destaque no Admin, o mais recente assume o topo.
  const mainPost = featured ?? posts[0];
  // A grade geral lista tudo, menos o artigo que já ocupa o bloco de destaque.
  const rest = posts.filter((post) => post.id !== mainPost?.id);
  const recent = rest.slice(0, RECENT_COUNT);

  if (!mainPost) return null;

  return (
    <div className="space-y-6 px-6">
      {/* Topo: destaque (2/3) + recentes (1/3), com a mesma altura no desktop */}
      <section className="grid gap-6 lg:grid-cols-3">
        <FeaturedCard post={mainPost} onRead={onRead} />
        {recent.length > 0 && <RecentList posts={recent} onRead={onRead} />}
      </section>

      {recommended.length > 0 && <RecommendedStrip posts={recommended} onRead={onRead} />}

      {rest.length > 0 && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((post) => (
            <PostCard key={post.id} post={post} onRead={onRead} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Faixa "Recomendados": camada intermediária entre o topo e a grade geral.
 * Cards compactos (miniatura + texto lado a lado) para não competir em altura
 * com o bloco de destaque nem com os cards da grade.
 */
function RecommendedStrip({ posts, onRead }: { posts: BlogPost[]; onRead: (slug: string) => void }) {
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
        <Sparkles className="h-4 w-4 text-primary" />
        Recomendados
      </h2>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {posts.map((post) => (
          <RecommendedCard key={post.id} post={post} onRead={onRead} />
        ))}
      </div>
    </section>
  );
}

function RecommendedCard({ post, onRead }: { post: BlogPost; onRead: (slug: string) => void }) {
  return (
    <div
      {...clickableCardProps(() => onRead(post.slug))}
      aria-label={post.title}
      className="group flex cursor-pointer items-stretch gap-3 overflow-hidden rounded-xl border bg-card transition-all duration-200 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <div className="relative h-[104px] w-[120px] shrink-0 overflow-hidden">
        {post.thumbnail_url ? (
          <img
            src={resolveMediaUrl(post.thumbnail_url)}
            alt={post.title}
            style={coverPositionStyle(post.cover_position)}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
            <BookOpen className="h-7 w-7 text-primary/40" />
          </div>
        )}
      </div>
      <div className="flex min-w-0 flex-col justify-center gap-1 py-3 pr-3">
        {post.category && (
          <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">
            {post.category}
          </span>
        )}
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground group-hover:text-primary">
          {post.title}
        </h3>
        {post.read_time_minutes ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            {post.read_time_minutes} min de leitura
          </span>
        ) : null}
      </div>
    </div>
  );
}

function FeaturedCard({ post, onRead }: { post: BlogPost; onRead: (slug: string) => void }) {
  return (
    <div
      {...clickableCardProps(() => onRead(post.slug))}
      aria-label={post.title}
      className="group relative h-[260px] cursor-pointer overflow-hidden rounded-2xl transition-all duration-200 hover:-translate-y-1 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:h-[320px] lg:col-span-2 lg:h-[380px]"
    >
      {post.thumbnail_url ? (
        <img
          src={resolveMediaUrl(post.thumbnail_url)}
          alt={post.title}
          style={coverPositionStyle(post.cover_position)}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
          <BookOpen className="h-14 w-14 text-primary/40" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 space-y-2 p-6 text-white">
        {post.category && (
          <span className="inline-block rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
            {post.category}
          </span>
        )}
        <h2 className="line-clamp-2 text-xl font-bold leading-tight md:text-2xl">{post.title}</h2>
        {post.excerpt && (
          <p className="line-clamp-2 max-w-2xl text-sm text-white/80">{post.excerpt}</p>
        )}
        {post.read_time_minutes ? (
          <span className="flex items-center gap-1.5 pt-1 text-xs text-white/70">
            <Clock className="h-3.5 w-3.5" />
            {post.read_time_minutes} min de leitura
          </span>
        ) : null}
      </div>
    </div>
  );
}

function RecentList({ posts, onRead }: { posts: BlogPost[]; onRead: (slug: string) => void }) {
  return (
    <aside className="flex flex-col overflow-hidden rounded-2xl border bg-card lg:h-[380px]">
      <h3 className="border-b px-4 py-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        Recentes
      </h3>
      <ul className="flex flex-1 flex-col divide-y divide-border">
        {posts.map((post) => (
          <li key={post.id} className="min-h-0 flex-1">
            <button
              type="button"
              onClick={() => onRead(post.slug)}
              className="group flex h-full w-full flex-col justify-center gap-1 px-4 py-3 text-left transition-colors hover:bg-muted/50"
            >
              {post.category && (
                <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">
                  {post.category}
                </span>
              )}
              <p className="line-clamp-2 text-sm font-semibold leading-snug text-foreground group-hover:text-primary">
                {post.title}
              </p>
              {post.read_time_minutes ? (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" />
                  {post.read_time_minutes} min de leitura
                </span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/* Card padrão (grid) e resultados                                    */
/* ------------------------------------------------------------------ */

function PostCard({ post, onRead }: { post: BlogPost; onRead: (slug: string) => void }) {
  return (
    <div
      {...clickableCardProps(() => onRead(post.slug))}
      aria-label={post.title}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-xl border bg-card transition-all duration-200 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {/* Capa 16:9 com teto de 190px: mantém a proporção em colunas estreitas
          e evita imagens altas demais no grid de 3 colunas. */}
      <div className="relative aspect-[16/9] max-h-[190px] w-full shrink-0 overflow-hidden">
        {post.thumbnail_url ? (
          <img
            src={resolveMediaUrl(post.thumbnail_url)}
            alt={post.title}
            style={coverPositionStyle(post.cover_position)}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
            <BookOpen className="h-10 w-10 text-primary/40" />
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4">
        {/* flex-1 empurra o rodapé para a base, alinhando os metadados
            entre cards de alturas diferentes na mesma linha do grid. */}
        <div className="flex-1 pb-4">
          {post.category && (
            <span className="mb-2.5 block w-fit rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
              {post.category}
            </span>
          )}
          <h3 className="line-clamp-2 text-[15px] font-semibold leading-snug text-foreground group-hover:text-primary">
            {post.title}
          </h3>
          {post.excerpt && (
            <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{post.excerpt}</p>
          )}
        </div>
        <PostMeta post={post} />
      </div>
    </div>
  );
}

/** Data de publicação e tempo de leitura numa única linha no rodapé do card. */
function PostMeta({ post }: { post: BlogPost }) {
  const publishedAt = post.created_at ? formatPublishedAt(post.created_at) : null;
  const readTime = post.read_time_minutes ?? null;

  if (!publishedAt && !readTime) return null;

  return (
    <div className="flex items-center gap-1.5 border-t pt-3 text-xs text-muted-foreground">
      {publishedAt && <span>{publishedAt}</span>}
      {publishedAt && readTime ? <span aria-hidden="true">•</span> : null}
      {readTime ? (
        <span className="flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" />
          {readTime} min de leitura
        </span>
      ) : null}
    </div>
  );
}

function ResultsView({
  posts,
  categories,
  activeCategory,
  searchTerm,
  totalPages,
  page,
  onPage,
  onClear,
  onRead,
}: {
  posts: BlogPost[];
  categories: BlogCategoryOption[];
  activeCategory?: string;
  searchTerm: string;
  totalPages: number;
  page: number;
  onPage: (updater: (p: number) => number) => void;
  onClear: () => void;
  onRead: (slug: string) => void;
}) {
  const categoryName = categories.find((c) => c.slug === activeCategory)?.name;
  const label = searchTerm
    ? `Resultados para “${searchTerm}”`
    : categoryName
      ? `Categoria: ${categoryName}`
      : 'Resultados';

  return (
    <div className="space-y-6 px-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{label}</p>
        <Button variant="ghost" size="sm" onClick={onClear} className="gap-1 text-primary">
          <X className="h-4 w-4" />
          Voltar ao Blog
        </Button>
      </div>

      {posts.length === 0 ? (
        <EmptyState message="Nenhum artigo encontrado. Tente ajustar sua busca ou filtro." />
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} onRead={onRead} />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" onClick={() => onPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            Anterior
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => onPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-xl border bg-card py-16 text-center">
      <BookOpen className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
      <h3 className="mb-2 text-lg font-medium">Nada por aqui ainda</h3>
      <p className="text-muted-foreground">{message}</p>
    </div>
  );
}
